import AppStoreBadge from '@/components/AppStoreBadge';

import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Calculator, TrendingUp, Shield, Zap, CheckCircle2, HelpCircle, XCircle } from 'lucide-react';
import { FuturisticCard } from '@/components/futuristic/FuturisticCard';
import { GradientBackground } from '@/components/enhanced/GradientBackground';
import { Button } from '@/components/ui/button';
import { images } from '@/assets/images';
import SEOHead from '@/components/seo/SEOHead';
import SEOFooter from '@/components/layout/SEOFooter';

const Home: React.FC = () => {
  const features = [
    {
      icon: Calculator,
      title: 'Smart Calculators',
      description: 'AI-powered mortgage and buying power calculations',
      color: 'purple' as const,
    },
    {
      icon: TrendingUp,
      title: 'Market Analytics',
      description: 'Real-time property market insights and trends',
      color: 'cyan' as const,
    },
    {
      icon: Shield,
      title: 'Secure Platform',
      description: 'Bank-level security for your financial data',
      color: 'green' as const,
    },
    {
      icon: Zap,
      title: 'Instant Results',
      description: 'Get comprehensive reports in seconds',
      color: 'orange' as const,
    },
  ];

  const howItWorks = [
    { step: '1', title: 'Enter Your Details', description: 'Input your home price, down payment, interest rate, and loan term into our mortgage payment calculator or reverse PITI calculator.' },
    { step: '2', title: 'Get AI-Powered Analysis', description: 'Our AI engine calculates your monthly mortgage payment breakdown — principal, interest, taxes, insurance, PMI, and HOA fees.' },
    { step: '3', title: 'Make Smart Decisions', description: 'Compare scenarios, analyze deals with SmartBuy, and get personalized guidance from CLARA, our AI homebuyer assistant.' },
  ];

  const faqs = [
    { q: 'How do I calculate my monthly mortgage payment?', a: 'Use our free mortgage payment calculator — enter your home price, down payment, interest rate, and loan term. EquiForge instantly calculates your total monthly payment including principal, interest, property taxes, homeowner\'s insurance, PMI, and HOA fees.' },
    { q: 'What is a PITI calculator?', a: 'PITI stands for Principal, Interest, Taxes, and Insurance — the four components of a monthly mortgage payment. Our PITI calculator breaks down each component so you understand exactly where your money goes each month.' },
    { q: 'How much house can I afford?', a: 'Use our reverse PITI calculator to find out. Enter your desired monthly payment and we\'ll calculate the maximum home price you can afford based on current interest rates, taxes, and insurance in your area.' },
    { q: 'Is EquiForge free to use?', a: 'Yes! Our mortgage calculator and reverse PITI calculator are completely free. Sign up for a free account to unlock SmartBuy deal analysis, AI-powered insights from CLARA, and advanced real estate analytics.' },
    { q: 'What makes EquiForge different from other mortgage calculators?', a: 'EquiForge combines accurate PITI calculations with AI-powered real estate analytics, property comparison tools, closing cost estimators, and a conversational AI assistant — all in one platform built for homebuyers and real estate professionals.' },
  ];

  return (
    <GradientBackground variant="primary" animated>
      <SEOHead
        title="EquiForge™ — AI Mortgage Calculator & Real Estate Analytics"
        description="Calculate your monthly mortgage payment with our free PITI calculator. AI-powered home affordability tools, real estate analytics, and investment analysis for smarter homebuying decisions."
        keywords="mortgage calculator, home loan calculator, PITI calculator, monthly mortgage payment, home affordability calculator, real estate analytics, AI mortgage tool, how much house can I afford, first time homebuyer, mortgage payment estimator"
        canonicalPath="/home"
      />
      <div className="duo-page min-h-dvh flex flex-col">
        {/* Fixed Header with Logo */}
        <header className="relative z-10 px-6 pt-6">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/" className="block">
              <img
                src={images.equiforgeLogoFlat}
                alt="EquiForge — AI Mortgage Calculator and Real Estate Analytics Platform"
                className="h-16 md:h-20 lg:h-24 w-auto object-contain drop-shadow-2xl hover:scale-105 transition-transform duration-300"
              />
            </Link>
          </motion.div>
        </header>

        {/* Hero Section */}
        <section className="flex-1 flex items-center justify-center px-6 py-10 md:py-16">
          <div className="max-w-6xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold">
                <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
                  EquiForge
                </span>
              </h1>
              
              <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto">
                The AI-Powered Mortgage Calculator & Real Estate Intelligence Platform
              </p>
              
              <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                Calculate your monthly mortgage payment, estimate home affordability, compare properties, 
                and analyze investment deals — all with cutting-edge AI technology built for 
                homebuyers and real estate professionals.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-12">
                <Button asChild size="lg" className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
                  <Link to="/signup">
                    Get Started Free
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </Button>
                
                <Button asChild variant="outline" size="lg" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                  <Link to="/login">
                    Sign In
                  </Link>
                </Button>
                
                <Button asChild variant="outline" size="lg" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                  <Link to="/try">
                    Try Free Mortgage Calculator
                  </Link>
                </Button>
              </div>
              <div className="flex justify-center">
                <AppStoreBadge />
              </div>
            </motion.div>
          </div>
        </section>

        {/* Features Section */}
        <section className="px-6 py-20">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-200 mb-4">
                Powerful Mortgage & Real Estate Tools
              </h2>
              <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                Everything you need to calculate mortgage payments, analyze deals, and make informed real estate decisions
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 * index }}
                >
                  <FuturisticCard glowColor={feature.color} className="p-6 h-full">
                    <div className="flex flex-col items-center text-center space-y-4">
                      <div className={`p-3 rounded-lg bg-${feature.color}-500/10 border border-${feature.color}-500/30`}>
                        <feature.icon className={`w-6 h-6 text-${feature.color}-400`} />
                      </div>
                      <h3 className="text-xl font-semibold text-slate-200">
                        {feature.title}
                      </h3>
                      <p className="text-slate-400">
                        {feature.description}
                      </p>
                    </div>
                  </FuturisticCard>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="px-6 py-20 bg-slate-900/50">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-200 text-center mb-4">How It Works</h2>
            <p className="text-slate-400 text-center mb-12 max-w-2xl mx-auto">
              Calculate your <Link to="/try/payment" className="text-purple-400 hover:underline">monthly mortgage payment</Link> or find out <Link to="/try/reverse-piti" className="text-cyan-400 hover:underline">how much house you can afford</Link> in three simple steps.
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              {howItWorks.map((item, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 * i }}>
                  <FuturisticCard glowColor="cyan" className="p-6 h-full text-center">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center text-xl font-bold">{item.step}</div>
                    <h3 className="text-lg font-semibold text-slate-200 mb-2">{item.title}</h3>
                    <p className="text-sm text-slate-400">{item.description}</p>
                  </FuturisticCard>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose EquiForge */}
        <section className="px-6 py-20">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-200 text-center mb-12">Why Choose EquiForge?</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                'Free mortgage payment calculator with full PITI breakdown',
                'Reverse PITI calculator — find your max home price from your budget',
                'AI-powered home affordability and debt-to-income analysis',
                'SmartBuy deal analyzer to compare properties side-by-side',
                'CLARA — conversational AI assistant for first-time homebuyers',
                'Closing cost estimator with state-specific data',
                'Real estate investment analytics and ROI projections',
                'SOC 2 Type II certified — bank-level data security',
              ].map((item, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}
                  className="flex items-start gap-3 p-3"
                >
                  <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-300">{item}</span>
                </motion.div>
              ))}
            </div>
            <div className="text-center mt-8">
              <Button asChild size="lg" className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700">
                <Link to="/try">
                  Try Free Calculator Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="px-6 py-20 bg-slate-900/50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-200 text-center mb-4">Frequently Asked Questions</h2>
            <p className="text-slate-400 text-center mb-12">Common questions about mortgage calculators, home affordability, and EquiForge</p>
            <div className="space-y-6">
              {faqs.map((faq, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 * i }}>
                  <FuturisticCard glowColor="purple" className="p-6">
                    <div className="flex items-start gap-3">
                      <HelpCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-lg font-semibold text-slate-200 mb-2">{faq.q}</h3>
                        <p className="text-sm text-slate-400 leading-relaxed">{faq.a}</p>
                      </div>
                    </div>
                  </FuturisticCard>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="px-6 py-20">
          <div className="max-w-4xl mx-auto text-center">
            <FuturisticCard glowColor="cyan" className="p-12">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="space-y-6"
              >
                <h2 className="text-3xl md:text-4xl font-bold text-slate-200">
                  Ready to Calculate Your Mortgage Payment?
                </h2>
                <p className="text-lg text-slate-400">
                  Join thousands of homebuyers and real estate professionals who trust EquiForge for accurate mortgage calculations, home affordability analysis, and AI-powered property insights.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button asChild size="lg" className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600">
                    <Link to="/signup">
                      Create Free Account
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                    <Link to="/brochure">
                      View Platform Brochure
                    </Link>
                  </Button>
                </div>
              </motion.div>
            </FuturisticCard>
          </div>
        </section>

        <SEOFooter />
      </div>
    </GradientBackground>
  );
};

export default Home;
